/**
 * ⭐ `AW16` · El borde HTTP contra **`apps/bot-web`** — el segundo proceso con el
 * que esta unidad habla, y el primero del plano PARTICIPANTE.
 *
 * El molde es `src/conversaciones/borde-conversaciones.ts` (`AW4`), que resuelve
 * el mismo problema contra el otro proceso: *«`contracts/conversation` fija el
 * puerto […] este archivo fija el cableado HTTP: la ruta, los nombres de los
 * parámetros de query y el revival de fechas»*. Acá pasa lo mismo con una
 * diferencia que se declara abajo: **del otro lado no hay contrato**.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⛔⛔ ①  ESTE ARCHIVO NO IMPORTA `modules/**`, Y ACÁ LA TENTACIÓN ES MAYOR
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * La implementación equivocada es importar `modules/channel` y llamar `receive()`
 * derecho, «total está en el mismo repo y me ahorro una capa HTTP». **Falla por
 * tres lados y ninguno avisa temprano:**
 *
 *   ① el grafo lo prohíbe textual —`web-dev ──✗──▶ cualquier modules/`—;
 *   ② **compila en el `vite` de quien lo escribe y no existe en producción**:
 *      esta unidad es un artefacto estático en S3 + CloudFront, «un bucket, no un
 *      proceso», y `receive()` necesita una `TransactionalUnit`, o sea **una
 *      conexión a Postgres desde el navegador**;
 *   ③ y el peor, que es propio de esta pantalla: **destruiría lo único que el
 *      simulador vale.** `ADR-0196` fija que el motor sea el REAL porque el
 *      simulador tiene que atravesar **el mismo camino que producción**; un
 *      `receive()` llamado desde el navegador no atraviesa `apps/bot-web` —ni su
 *      transacción, ni su lectura del cuerpo, ni su ruteo de brazo— así que
 *      **ejercería un camino que no existe** y daría verdes sobre nada.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⚠️ ②  DEL OTRO LADO NO HAY CONTRATO, Y ESO VA DECLARADO Y NO TAPADO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `AW4` pudo importar `ConsultaDeMensajes`/`ResultadoDeMensajes` de
 * `contracts/conversation`. **Acá no hay equivalente**: el cuerpo del `POST` de
 * entrada lo arma `apps/bot-web/src/main.ts` (`leerNotificacion`) con nombres
 * propios en castellano —`canal`, `de`, `providerMessageId`, `tipo`, `texto`,
 * `interactivo`—, y `Entrega`/`PaginaDeEntregas` viven en
 * `apps/bot-web/src/entregas.ts`, que **no es un contrato**. Importarlos sería un
 * `import` de una app a otra, que ni el `tsconfig` de acá resuelve ni el grafo
 * admite. ⇒ **las formas se declaran acá abajo, y son una SEGUNDA declaración de
 * algo que ya existe en el disco.** El día que `bot-web` le cambie un nombre a un
 * campo, esto sigue compilando y deja de andar. **Hueco declarado, dueño
 * `apps-integrator` + `web-dev`** — es el mismo que `borde-conversaciones.ts` ya
 * declaró («no existe `contracts/conversation/http.ts`»), un grado peor porque
 * allá el tipo sí es compartido y acá no.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * ⭐ `AW56` (16/9/2026) · **QUÉ MITAD DE ESTE HUECO SE LEVANTÓ Y CUÁL SIGUE ACÁ**
 * ───────────────────────────────────────────────────────────────────────────
 *
 * El párrafo de arriba **no se borra y no se declara resuelto** (`ADR-0082`:
 * redactar no es borrar). Sigue siendo literalmente cierto, y por eso queda con
 * su fecha y su dueño. Lo que cambió es **una sola mitad**, y va escrita para que
 * el que venga a diagnosticar no descarte el borde por lo que decía el comentario:
 *
 *   · **LEVANTADO — el fallo dejó de ser MUDO.** `entregas` ya no normaliza en
 *     silencio: comprueba el cuerpo campo por campo y **lanza** con la misma
 *     disciplina que `enviar` ejerce sobre `POST /entrada`. Un renombre de
 *     `bot-web` **ya no se dibuja como burbujas vacías**: produce un error
 *     nombrado, en el primer pedido, con el campo y la posición.
 *   · **SIGUE ACÁ — `tsc` sigue ciego.** No hay `import` que conecte los dos
 *     lados, el `tsconfig` de esta app **no puede resolver `apps/`** (su único
 *     `paths` es `@contracts/*`), y las dos declaraciones siguen duplicadas en
 *     dos archivos que no se conocen. ⇒ **un renombre SIGUE COMPILANDO y SIGUE
 *     LLEGANDO A PRODUCCIÓN.** Lo que `AW56` entrega es un **detector de
 *     runtime**, no una garantía estática, y no se declara de otra manera.
 *   · **SIGUE ACÁ — el cierre real es un `contracts/channel/http.ts`**, que hoy
 *     no existe (hay cinco `http.ts` bajo `contracts/`, uno por dominio, y
 *     `channel` es el que quedó afuera — el nombre del directorio comodín no se
 *     escribe acá porque la barra y el asterisco juntos CIERRAN este comentario).
 *     Cruza tres territorios y tiene una restricción de build: el
 *     alias `@contracts/*` **no está en `vite.config.ts` a propósito** (ver ⛔
 *     abajo), así que ese contrato tiene que ser consumible como `import type`
 *     puro. **Dueño: `apps-integrator` + quien escriba `contracts/channel`.**
 *
 * ⛔ **Y por eso este archivo tampoco importa VALORES de `contracts/**`.** Los
 * dos literales de tipo de entrante existen —`INBOUND_TEXT = "texto"` y
 * `INBOUND_INTERACTIVE = "interactivo"`, `contracts/channel/index.ts`— y **se
 * exportan como valor**, pero `vite.config.ts` no tiene alias hacia `contracts/`
 * a propósito: *«un import de VALOR desde `contracts/` hace fallar el build. Es la
 * regla de arquitectura sostenida por la herramienta en vez de por la memoria de
 * quien edita»*. ⇒ acá se escriben, con la cita al lado, y la duplicación va
 * declarada en el informe en vez de resolverse rompiendo el build.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ⭐⭐ ③  `POST /entrada` SE REUSA. NO HAY RUTA NUEVA. `ADR-0198`
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `ADR-0198` (`accepted`, 26/8/2026) **revocó `D11`**, que pedía una ruta propia
 * para el simulador. Su premisa era falsa —el desenlace **ya viaja en el cuerpo**
 * del `200`, `main.ts:303` `Response.json({ outcome: desenlace })`— y el argumento
 * que decide es el conteo: *«Sobre un proceso que no autentica a nadie, el número
 * de rutas ES la superficie. Reusar `/entrada` suma CERO. Una ruta propia sumaba
 * una.»* ⇒ **este archivo habla contra las rutas que ya existen y no pide
 * ninguna.**
 */

import type { ConfiguracionInstancia } from "../configuracion.ts"
import { mensajeDeError } from "../mensaje-de-error.ts"

/**
 * El tipo de entrante que ejecuta el turno del núcleo.
 *
 * ⚠️ **Copia declarada de `INBOUND_TEXT` (`contracts/channel/index.ts`)**, por el
 * bloque ② del encabezado: el contrato lo exporta como VALOR y un import de valor
 * desde `contracts/` rompe el build de esta unidad, por diseño.
 */
export const TIPO_TEXTO = "texto"

/**
 * El tipo de entrante que lleva un botón.
 *
 * ⚠️ Copia declarada de `INBOUND_INTERACTIVE` (`contracts/channel/index.ts`), por
 * el mismo motivo que `TIPO_TEXTO`.
 */
export const TIPO_INTERACTIVO = "interactivo"

/** El cursor que significa «todo lo que haya» (`apps/bot-web/src/entregas.ts`). */
export const CURSOR_INICIAL = "0"

/**
 * Lo que se manda por `POST /entrada`, con los nombres que `bot-web` lee.
 *
 * ⛔ **No hay campo de brazo, y no puede haberlo.** `apps/bot-web/src/main.ts`
 * deriva el brazo del **canal receptor** —`brazoDeLaConsulta` para las lecturas,
 * `InboundNotification.receivedOn` para la escritura— y contesta
 * `canal-no-ruteable` si `ruteo.armOf` da `null`. ⇒ la pantalla manda un canal y
 * el brazo sale de ahí.
 */
export interface EntranteASimular {
  /** El canal receptor. De acá sale el brazo, y de ningún otro lado. */
  readonly canal: string
  /**
   * La dirección con la que el participante escribe.
   *
   * ⛔ **Es PII.** `modules/channel/src/entrada/recibir.ts` corta con
   * `"emisor-desconocido"` **antes de persistir y antes de encolar** si no está en
   * el padrón del brazo, así que un valor que no salga del padrón produce ese
   * desenlace y nada más — que es el motor real diciendo la verdad, no un defecto.
   */
  readonly de: string
  /**
   * El identificador del mensaje del proveedor. **Repetirlo produce `duplicado`.**
   */
  readonly providerMessageId: string
  /** `TIPO_TEXTO`, `TIPO_INTERACTIVO`, o cualquier otro ⇒ `modalidad`. */
  readonly tipo: string
  /** El texto, o `null`. Un `tipo` de texto con `texto: null` cae en `modalidad`. */
  readonly texto: string | null
  /**
   * El botón, o `null`. El identificador es opaco: ver `PantallaSimulador`.
   *
   * ⭐⭐⭐ `R2-UI9` · **LAS CLAVES SON LAS DEL CONTRATO, LETRA POR LETRA.**
   * `[medido 23/9/2026 contra `apps/bot-web/src/main.ts` ⟦el-lector-del-cuerpo-plano⟧]`
   * el lector lee **`buttonId`**, **`text`** y **`contextMessageId`** —y ningún otro
   * deletreo—, que son los tres nombres de `InboundInteractive` en
   * `contracts/channel`. Hasta `R2-BW2` el lector aceptaba `texto` y
   * `contextoDelMensaje`; **esas dos claves fueron RETIRADAS** y escribirlas acá
   * hoy dejaría el toque en `interactivo-sin-ruteo` **sin que nada falle**:
   * `ADR-0346` / `ADR-0338` lo miden —*«el cuerpo viaja como JSON por HTTP y las
   * dos mitades compilan solas»*—. ⛔ Cualquier cambio de estos nombres hay que
   * verificarlo CONTRA `apps/bot-web/src/`, no contra un encargo.
   *
   * ⛔⛔ **`text` ES EL TEXTO VISIBLE DEL BOTÓN, Y NO EL ID.** `RT-2`: el
   * identificador de un botón de Meta **no coincide con nada de lo que trae la
   * sincronización**, así que mandar el id como etiqueta haría que la búsqueda de
   * la respuesta automática no encontrara la fila y el desenlace fuera
   * `boton-sin-respuesta` — *«un diagnóstico FALSO sobre una plantilla que sí la
   * tiene»*.
   *
   * ⭐ **`""` es legítimo y significa «no sé el texto visible».** El lector de
   * `bot-web` exige `text` no vacío; con la cadena vacía `interactive` queda `null`
   * y el toque **falla cerrado** en `interactivo-sin-ruteo`, que es observable,
   * contable y verdadero. Es lo que deja vivo el escape del redactor —*«el único
   * camino para producir `interactivo-sin-ruteo` a pedido. No se saca.»*— en vez
   * de convertirlo en un `boton-sin-respuesta` que nadie pidió.
   *
   * ⚠️ **`contextMessageId` NO se manda** (`r2-ui9.md`, decisión 4): queda `null`
   * del lado de `bot-web`. El lector de allá tiene la rama igual, para que el
   * camino del Simulador sea el MISMO que el del sobre real (`ADR-0196`).
   */
  readonly interactivo: { readonly buttonId: string; readonly text: string } | null
}

/**
 * Una entrega saliente, tal como la proyecta `apps/bot-web/src/entregas.ts`.
 *
 * ⛔⛔ **`destinatario` es EL TELÉFONO DEL PARTICIPANTE EN CLARO** (`aEntrega`:
 * `destinatario: fila.telefono`). Está en el tipo porque está en el cable, **no
 * porque la pantalla lo tenga que dibujar** — y no lo dibuja: ver `chat.tsx`.
 */
export interface EntregaEnLaRed {
  readonly fecha_hora: string
  readonly contenido: string
  readonly destinatario: string
  readonly id_usuario: string
  readonly id_conversacion: string
  readonly id_sesion: string
  readonly fuentes_consultadas: number | null
}

export interface PaginaDeEntregasEnLaRed {
  readonly entregas: readonly EntregaEnLaRed[]
  /** El cursor para el próximo pedido. **Siempre presente**, aun sin entregas. */
  readonly cursor: string
  /** `true` si el tope de páginas cortó la lectura y hay más ya disponible. */
  readonly hayMas: boolean
}

/**
 * ⭐⭐ RASTRO · `ADR-0292` / `B22` · 16/9/2026 — **EL REGISTRO INTERNO DE TURNOS
 * TIENE BORDE ACÁ, Y NADA DE LO DE ARRIBA SE TOCA.**
 *
 * QUÉ: `GET /turnos` de `apps/bot-web` proyecta `conversation.turn_log` —el registro
 * que el motor escribe por CADA turno: el camino que tomó, cuánto tardó, en cuántos
 * fragmentos contestó, qué dijo el juez, si moderó, si se contuvo—.
 *
 * POR QUÉ: `ADR-0292` abrió el endpoint y el bloqueante `B22` de QA fija la otra
 * mitad, textual: *«endpoints de sólo lectura, **y además el Simulador los muestra**
 * — un acceso que sólo consume el runner es un acceso que nadie mira»*. ⇒ el borde
 * existe **para que la pantalla lo dibuje**, no para que lo consuma un script.
 *
 * ⛔ **Sigue sin haber contrato del otro lado** —bloque ② del encabezado, entero—: lo
 * de acá abajo es una **SEGUNDA declaración** de lo que `bot-web` ya tiene en el
 * disco. El día que renombre `ms_transcurridos`, esto compila igual y deja de andar.
 * Hueco declarado, dueño `apps-integrator` + `web-dev`, el mismo que `EntregaEnLaRed`.
 *
 * ⛔ **Y tampoco estrena disciplina**: mismo `SIN_CREDENCIALES`, mismo `errorDelBorde`,
 * mismo `mensajeDeError` que las dos lecturas que ya estaban.
 */

/**
 * Una fila del registro de turnos, **con los nombres del cable y sin traducir**
 * (`snake_case` tal cual llega), igual que `EntregaEnLaRed`.
 *
 * ⛔⛔ **NO TRAE PARTICIPANTE NI CONVERSACIÓN, Y NO ES UN OLVIDO: NO HAY COLUMNA.**
 * `turn_log` es el registro del MOTOR para el brazo entero. ⇒ correlacionar una fila
 * de acá con una entrega **sólo se puede por brazo y por tiempo**, y eso tiene un
 * límite conocido que `chat.tsx` declara donde lo ejerce. Acá se dice para que nadie
 * lo lea como una llave: **no hay llave**.
 *
 * ⚠️ **La fila se escribe ASÍNCRONA**: medido contra el servicio vivo, llega 1-3 s
 * después de la entrega. Una lectura que no la encuentra todavía **no significa que
 * no exista**, y el que consume esto no debe rellenar nada.
 */
export interface TurnoEnLaRed {
  /** El cursor del registro. **Ascendente y ralo**: el brazo se lleva sólo los suyos. */
  readonly seq: number
  /** ISO-8601, tal como vino. ⛔ No se reformatea acá: el borde transporta. */
  readonly fecha_hora: string
  /** El camino que tomó el turno, **crudo** —`respondido`, `contencion`, `sin-cobertura`…—. */
  readonly camino: string
  readonly fallback: boolean
  readonly fragmentos: number
  readonly ms_transcurridos: number
  /** `null` cuando no hubo juez. ⛔ No se rellena con `0`: son cosas distintas. */
  readonly judge_score: number | null
  readonly reintentos_del_modelo: number
  readonly regeneraciones: number
  readonly veredicto_del_juez: string | null
  readonly resultado_de_moderacion: string | null
  /** La categoría de contención que se disparó, o `null`. */
  readonly categoria_disparada: string | null
}

export interface PaginaDeTurnosEnLaRed {
  readonly turnos: readonly TurnoEnLaRed[]
  /**
   * El `seq` hasta el que se leyó. **Siempre presente**, aun sin turnos — y cuando la
   * página sale vacía vale el `desde` que se pidió, así que sirve igual de cursor.
   */
  readonly ultimoSeq: number
  /** `true` si el tope de la página cortó la lectura y hay más ya disponible. */
  readonly hayMas: boolean
}

/**
 * Lo que se le pide a `GET /turnos`.
 *
 * ⛔ **`desde` es EXCLUSIVO y está MEDIDO** `[16/9/2026]` contra el servicio vivo:
 * `desde=1047` sobre un registro cuyo último `seq` es 1047 devuelve `turnos: []`.
 * ⇒ pasar el `ultimoSeq` de la página anterior **no repite la última fila**, que es
 * justo lo que hace falta para sondear sin duplicar.
 *
 * ⛔ **No hay «pedir el último»**: el registro ordena **ASCENDENTE**, así que
 * `desde=0&limite=1` trae el turno **MÁS VIEJO** y no el más nuevo. Quien quiera
 * ponerse al día tiene que paginar hasta `hayMas === false`. Ver `chat.tsx`.
 */
export interface ConsultaDeTurnos {
  /** El canal receptor. De acá sale el brazo, igual que en todo el resto del borde. */
  readonly canal: string
  /** El `seq` desde el que leer, **exclusivo**. Entero ≥ 0 o el servicio contesta `400`. */
  readonly desde: number
  /** Cuántas filas como mucho. Entero ≥ 0 y **tope 500**, o el servicio contesta `400`. */
  readonly limite: number
}

/**
 * El tope de página que el servicio admite, **medido** `[16/9/2026]`: con
 * `limite=1000` contesta `400` con *«el parámetro "limite" no puede pasar de 500»*.
 *
 * ⭐ Se escribe acá y no en la pantalla **porque es del cable**: es el servicio el que
 * lo impone, igual que `CURSOR_INICIAL`. Una pantalla que lo eligiera pondría el
 * número en el lugar donde nadie lo puede relacionar con el `400` que produce.
 */
export const LIMITE_DE_TURNOS = 500

export type BordeSimulador = {
  /**
   * Manda un entrante y devuelve **el desenlace crudo** que el motor contestó.
   *
   * ⛔⛔ **Devuelve `string` y NO `InboundOutcome`, a propósito.** Narrowear al
   * tipo del contrato exigiría comparar contra la lista de los ocho
   * (`INBOUND_OUTCOMES`), que es un VALOR y no se puede importar acá (bloque ② del
   * encabezado) ⇒ habría que **copiar la lista**, y una lista copiada convierte al
   * noveno desenlace en un hueco: el día que `contracts/channel` sume uno, esta
   * pantalla lo escondería justo cuando alguien lo está buscando. **Lo que el
   * motor dijo se devuelve tal cual, sin verificar contra nada.**
   */
  readonly enviar: (entrante: EntranteASimular) => Promise<string>

  /**
   * ⭐⭐ Lee las salientes del brazo, **ACOTADAS AL PARTICIPANTE**.
   *
   * ⛔⛔ **`participante` ES UN ARGUMENTO OBLIGATORIO, Y ÉSA ES TODA LA DEFENSA.**
   *
   * `AP30` agregó `?participante=` a `GET /entregas` y `GET /sse` y **lo dejó
   * OPCIONAL en el cable a propósito**, para no romper a `curl`, que es el
   * consumidor vivo que `ADR-0027` nombra. `apps/bot-web/src/main.ts` lo escribió
   * así: *«el costo residual, dicho sin taparlo: con el simulador encendido, un
   * origen admitido puede pedir `/entregas` SIN el parámetro y recibir el brazo.
   * Lo que cierra ese hueco es del lado del consumidor —`AW16`, cuyo borde toma el
   * participante como argumento obligatorio de tipo—.»*
   *
   * ⇒ **la implementación equivocada no se ve como un error: es no pasar un
   * parámetro opcional.** Un `fetch` que arma la query con el `canal` y el
   * `cursor` y se olvida del participante **anda perfecto, devuelve más filas, y
   * ninguna capa se queja**. Lo que trae de más es `destinatario`, o sea **el
   * teléfono en claro de cada participante del brazo**, sobre un padrón de ~16.000
   * estudiantes chilenos, muchos menores. **El síntoma no existe**: hay burbujas,
   * están bien formadas, y son de otras conversaciones.
   *
   * ⇒ por eso la defensa es **de TIPO y no de disciplina**: una sola función, con
   * el participante como argumento requerido, para que `tsc` señale a cualquier
   * llamador que no lo tenga.
   *
   * ⛔ **Las dos variantes que NO valen:** filtrar en el componente las burbujas
   * que no son del participante —la PII ya cruzó el cable y ya está en el
   * navegador— y pedir sin filtro «sólo mientras desarrollo», que es la forma en
   * que esto sobrevive a una revisión.
   *
   * ⚠️ **Ningún examen de este repo ve nada de esto, y no se dice que lo vea.**
   * Los casos de `pruebas/aw16-simulador.test.tsx` afirman que la URL lleva el
   * parámetro; no hay gate. Riesgo vivo, dueño `web-dev` + `apps-integrator`.
   *
   * ⛔ **`AW56` · LANZA ante un cuerpo desencontrado, y no devuelve una página
   * de relleno.** Un campo que `bot-web` renombró produce un error nombrado —con
   * el campo y la posición, y **sin citar ningún valor**— en vez de burbujas
   * vacías. Quien llame a esto tiene que estar preparado para el `throw`, igual
   * que ya lo estaba para el no-`200` y para la caída de red.
   */
  readonly entregas: (
    canal: string,
    participante: string,
    cursor: string,
  ) => Promise<PaginaDeEntregasEnLaRed>

  /**
   * ⭐⭐ `ADR-0292` / `B22` · **Lee el registro interno de turnos DEL BRAZO.**
   *
   * ⛔⛔ **NO lleva participante, y la ausencia NO es la del acotamiento de
   * `entregas`.** Allá el parámetro es la única defensa contra traer el teléfono en
   * claro de todo el brazo; **acá no hay nada que acotar**: `turn_log` no tiene
   * columna de participante ni de conversación, así que no existe el pedido «sólo
   * los míos» que se pueda olvidar. ⇒ el argumento no falta: **no hay**.
   *
   * ⚠️ **Y por eso esta lectura es del BRAZO y se dice así donde se dibuja.** Lo que
   * devuelve son los turnos de todos los participantes del brazo; ninguno trae dato
   * de persona —camino, milisegundos, fragmentos, puntajes—, que es lo que hace que
   * mostrarla no repita el problema que `entregas` tiene que evitar.
   *
   * ⚠️ Los desenlaces del cable, tal como el servicio los contesta: `409` si el canal
   * es de otro brazo (V6), `404` si no rutea, `400` si `desde`/`limite` no son
   * enteros ≥ 0 o si `limite` pasa de `LIMITE_DE_TURNOS`. **Los tres lanzan**, con el
   * `statusCode` puesto, igual que las otras dos lecturas de este archivo.
   */
  readonly leerTurnos: (consulta: ConsultaDeTurnos) => Promise<PaginaDeTurnosEnLaRed>
}

/** Error del borde con la forma que el resto de la app ya usa (`HttpError`). */
function errorDelBorde(mensaje: string, statusCode: number): Error & { statusCode: number } {
  return Object.assign(new Error(mensaje), { statusCode })
}

/**
 * ⭐⭐ `AW56` · El error de un cuerpo de `GET /entregas` desencontrado.
 *
 * ⛔⛔ **NO CITA EL VALOR DE NINGÚN CAMPO, Y ESO NO ES PROLIJIDAD.**
 * `destinatario` es **el teléfono del participante EN CLARO** (ver
 * `EntregaEnLaRed`) y `contenido` es **el mensaje de un participante**, sobre un
 * padrón de ~16.000 estudiantes, muchos menores. Un `JSON.stringify(entrega)`
 * «para ayudar a depurar» pone PII en la consola del navegador, en el renglón de
 * error que la pantalla dibuja y en la captura con la que se reporta el
 * incidente. Es exactamente lo que ⟦el-500-no-cuenta-el-cuerpo⟧ prohíbe del otro
 * lado del cable, cometido de este lado. Hay gate: `gates/pii.spec.ts`.
 *
 * ⇒ el mensaje nombra **qué campo** y **en qué posición de la página**, que es
 * todo lo que hace falta para ir a mirar el cable, y **nada de lo que hay
 * adentro**.
 */
function errorDeCampo(
  campo: string,
  posicion: number,
  cuantas: number,
  statusCode: number,
): Error & { statusCode: number } {
  return errorDelBorde(
    `bot-web contestó ${statusCode} con una entrega cuyo campo \`${campo}\` no vino con la forma que este borde espera (entrega ${posicion} de ${cuantas}). El hilo no se puede mostrar y no se inventa. El valor no se cita: por este cable viajan datos de participantes.`,
    statusCode,
  )
}

/**
 * Un campo que el cable declara `string`. **Ausente, `null` o de otro tipo ⇒
 * LANZA**, y no se rellena con `""`.
 *
 * ⛔ Un `?? ""` acá es la implementación equivocada que esta función existe para
 * impedir: produce **exactamente el objeto que `AW56` vino a eliminar** —una
 * burbuja vacía dibujada como si el sistema hubiera entregado algo—, que es la
 * clase de confirmación que `ADR-0040 §②` prohíbe por nombre.
 *
 * ⚠️ Lo que **sí** admite, y va dicho: la cadena **vacía**. Se comprueba el TIPO
 * y no el contenido, porque un `contenido` vacío es un dato del otro proceso y no
 * un desencuentro del cable; el desencuentro que esta etapa repara es un campo
 * que **no está**, y ése llega como `undefined`.
 */
function campoDeTexto(
  entrega: Record<string, unknown>,
  campo: string,
  posicion: number,
  cuantas: number,
  statusCode: number,
): string {
  const valor = entrega[campo]
  if (typeof valor !== "string") throw errorDeCampo(campo, posicion, cuantas, statusCode)
  return valor
}

/**
 * Un campo que el cable declara `number | null` — hoy, `fuentes_consultadas`.
 *
 * ⛔⛔ **`null` PASA y `undefined` LANZA, y la diferencia es todo el punto.**
 * `null` es un valor que `bot-web` manda a propósito —«NO APLICA», y es distinto de
 * `0`— y la pantalla sabe dibujarlo como ausencia. `undefined` es **el campo que no
 * vino**, o sea el renombre. Tratarlos igual (un `?? null`) volvería a hacer mudo
 * justo el caso que se está buscando.
 *
 * ⛔ Y **`0` no se toca**: es un valor legítimo y medido.
 *
 * ⚠️ Se comprueba el TIPO y no el rango: el rango lo declara **el otro proceso**, y
 * rechazarlo acá dejaría la pantalla en blanco por un dato que igual se puede
 * dibujar. Ver el informe de `AW56`.
 *
 * ⚠️ **[ENMENDADO EL 16/9/2026.]** `ADR-0082`: acá decía *«—`fuentes_consultadas` y
 * `ms_desde_el_entrante`—»* y *«que `ms_desde_el_entrante` sea entero y ≥ 0 lo
 * declara el otro proceso»*. Al retirarse `AP46`/`AW57` quedó **un solo** llamador.
 * ⛔ La función **NO se especializa** en él: sigue tomando el nombre del campo por
 * parámetro y sigue sin saber cuál es, que es lo que la deja lista para el próximo
 * `number | null` sin aflojar ninguna de sus tres discriminaciones (`null` pasa,
 * `undefined` lanza, no-número lanza).
 */
function campoNumericoONulo(
  entrega: Record<string, unknown>,
  campo: string,
  posicion: number,
  cuantas: number,
  statusCode: number,
): number | null {
  const valor = entrega[campo]
  if (valor === null) return null
  if (typeof valor !== "number") throw errorDeCampo(campo, posicion, cuantas, statusCode)
  return valor
}

/**
 * ⭐⭐ `AW56` · Una entrega del cable, **comprobada campo por campo**.
 *
 * ⭐ **Se arma un objeto NUEVO en vez de dejar pasar el crudo, y las dos
 * propiedades que salen de eso son la mitad del valor de esta función:**
 *
 *   ① **`tsc` exige que estén LOS SIETE.** El literal de abajo se declara
 *      `EntregaEnLaRed`, así que el día que alguien le agregue un octavo campo al
 *      tipo **esto no compila** («Property … is missing»). Es la única atadura
 *      estática que hay entre la forma declarada y la forma comprobada: sin ella,
 *      la comprobación se queda atrás del tipo en silencio, que es la misma especie
 *      de hueco que `AW56` repara.
 *   ② **Lo que el cable traiga de MÁS no llega a la pantalla.** Un campo nuevo del
 *      servidor **no rompe** —no se rechaza lo desconocido— pero tampoco viaja al
 *      navegador hasta que este archivo lo declare.
 *
 * ⚠️ **[ENMENDADO EL 16/9/2026 — VOLVIERON A SER SIETE.]** `ADR-0082`: no se borra,
 * se cita textualmente lo que dejó de ser cierto. Acá decía *«`tsc` exige que estén
 * **LOS OCHO**»*, *«el día que alguien le agregue un **noveno** campo al tipo —como
 * `AW57` agregó el octavo—»*, y el ② cerraba con el ejemplo *«Es lo que pasó entre
 * `AP46` (que agregó `ms_desde_el_entrante` del lado de `bot-web`) y `AW57` (que lo
 * declaró de este lado): un borde que rechazara los campos de más habría dejado la
 * pantalla en blanco en el medio»*.
 *
 * `AP46` y `AW57` **se retiraron del árbol**: el dueño se quedó con la
 * implementación de `origin/master`, que mide OTRO intervalo desde otra fuente
 * (`ms_transcurridos`, por `GET /turnos`). El campo se fue del cable y de este tipo,
 * y la cuenta hizo el recorrido **7 → 8 → 7**.
 *
 * ⭐⭐ **Lo que NO cambió es el MECANISMO, y es todo el punto:** la comprobación se
 * **ACHICÓ con el tipo, no se apagó**. Sigue habiendo exactamente un campo
 * comprobado por cada campo declarado, `tsc` sigue exigiendo esa correspondencia en
 * las dos direcciones, y el ② sigue valiendo palabra por palabra — lo único que
 * perdió es el ejemplo histórico que lo ilustraba, que queda citado acá arriba.
 */
function entregaDelCable(
  crudo: unknown,
  posicion: number,
  cuantas: number,
  statusCode: number,
): EntregaEnLaRed {
  if (typeof crudo !== "object" || crudo === null || Array.isArray(crudo)) {
    throw errorDelBorde(
      `bot-web contestó ${statusCode} y la entrega ${posicion} de ${cuantas} no es un objeto. El hilo no se puede mostrar y no se inventa. El valor no se cita: por este cable viajan datos de participantes.`,
      statusCode,
    )
  }

  const campos = crudo as Record<string, unknown>

  return {
    fecha_hora: campoDeTexto(campos, "fecha_hora", posicion, cuantas, statusCode),
    contenido: campoDeTexto(campos, "contenido", posicion, cuantas, statusCode),
    destinatario: campoDeTexto(campos, "destinatario", posicion, cuantas, statusCode),
    id_usuario: campoDeTexto(campos, "id_usuario", posicion, cuantas, statusCode),
    id_conversacion: campoDeTexto(campos, "id_conversacion", posicion, cuantas, statusCode),
    id_sesion: campoDeTexto(campos, "id_sesion", posicion, cuantas, statusCode),
    fuentes_consultadas: campoNumericoONulo(
      campos,
      "fuentes_consultadas",
      posicion,
      cuantas,
      statusCode,
    ),
  }
}

/**
 * ⛔ **`credentials` NO se pone en `include`, al revés que en los otros bordes.**
 *
 * Los bordes contra `admin-web` mandan la cookie de sesión porque del otro lado
 * hay una sesión (Better Auth + MFA). **`bot-web` no autentica a nadie** —lo dice
 * en cinco archivos, `ADR-0197` §Costo ①— así que no hay credencial que mandar, y
 * mandarla igual sería filtrar la cookie del plano HUMANO al proceso del plano
 * PARTICIPANTE. Se deja en el default (`same-origin`), que con un origen distinto
 * significa no mandar nada.
 */
const SIN_CREDENCIALES = { credentials: "same-origin" } as const

export function crearBordeSimuladorHttp(botWebUrl: string): BordeSimulador {
  const raiz = botWebUrl

  return {
    enviar: async (entrante: EntranteASimular): Promise<string> => {
      let respuesta: Response
      try {
        respuesta = await fetch(`${raiz}/entrada`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          ...SIN_CREDENCIALES,
          /**
           * Los nombres son los que `leerNotificacion` lee, y el orden no importa.
           * `texto` e `interactivo` viajan **siempre**, en `null` cuando no
           * corresponden: es el mismo criterio con el que `contracts/channel`
           * declara `text: string | null` en vez de opcional —*«un campo ausente
           * se lee "no vino texto" igual que un campo en `null`, pero sólo el
           * segundo obliga a `tsc` a que alguien lo mire»*—.
           */
          body: JSON.stringify({
            canal: entrante.canal,
            de: entrante.de,
            providerMessageId: entrante.providerMessageId,
            tipo: entrante.tipo,
            texto: entrante.texto,
            interactivo: entrante.interactivo,
          }),
        })
      } catch (causa) {
        throw Object.assign(errorDelBorde(`No se pudo hablar con bot-web (${raiz}).`, 0), {
          cause: causa,
        })
      }

      /**
       * ⚠️ **Un no-`200` NO tiene desenlace, y no se le inventa uno.**
       *
       * `bot-web` contesta `200` en **todos** los desenlaces del canal —`errata.md`
       * §E3: Meta reintenta con backoff hasta 7 días ante un no-`2xx`— así que lo
       * único que llega por acá es un cuerpo ilegible (`400`) o una caída (`500`,
       * de cuerpo fijo por ⟦el-500-no-cuenta-el-cuerpo⟧, *«por este proceso pasan
       * mensajes de menores»*). **En los dos casos el entrante no produjo un
       * desenlace**, y devolver uno de relleno sería exactamente la clase de
       * confirmación que `ADR-0040 §②` prohíbe. Se lanza, y la pantalla lo dibuja
       * como lo que es: el pedido no llegó.
       */
      if (!respuesta.ok) {
        throw errorDelBorde(
          await mensajeDeError(
            respuesta,
            `bot-web respondió ${respuesta.status} al recibir el entrante.`,
          ),
          respuesta.status,
        )
      }

      const cuerpo = (await respuesta.json()) as { readonly outcome?: unknown }

      /**
       * ⛔ **El desenlace se LEE del cuerpo del `200` y no se deriva de nada más.**
       * Ni del texto de la respuesta, ni de si hubo respuesta, ni de un
       * temporizador: cualquier derivación es una **segunda opinión** sobre algo
       * que el motor ya dijo, y discrepa justo en los casos que más importan
       * —`duplicado` y `modalidad` producen una respuesta y `encolado` no produce
       * ninguna al instante, así que una derivación los clasifica al revés—.
       *
       * Un cuerpo sin `outcome` **no se rellena**: se lanza. Es la misma línea que
       * `borde-restricciones.test.ts` ya defendió del otro lado: un `as` que no
       * comprueba nada en runtime es donde se pierde el dato.
       */
      if (typeof cuerpo.outcome !== "string" || cuerpo.outcome === "") {
        throw errorDelBorde(
          `bot-web contestó 200 sin \`outcome\` en el cuerpo. El desenlace no se puede mostrar y no se inventa.`,
          respuesta.status,
        )
      }

      return cuerpo.outcome
    },

    entregas: async (
      canal: string,
      participante: string,
      cursor: string,
    ): Promise<PaginaDeEntregasEnLaRed> => {
      const url = new URL(`${raiz}/entregas`)
      url.searchParams.set("canal", canal)
      /**
       * ⭐⭐ El acotamiento. **Se escribe siempre y no se condiciona a nada**: no
       * hay `if (participante !== "")` acá, porque un participante vacío no es «sin
       * filtro» sino un llamador que no tiene con quién conversar, y ése no
       * llega hasta acá (ver `chat.tsx`: sin participante no hay sondeo).
       */
      url.searchParams.set("participante", participante)
      url.searchParams.set("cursor", cursor)

      let respuesta: Response
      try {
        respuesta = await fetch(url, {
          method: "GET",
          headers: { Accept: "application/json" },
          ...SIN_CREDENCIALES,
        })
      } catch (causa) {
        throw Object.assign(errorDelBorde(`No se pudo hablar con bot-web (${raiz}).`, 0), {
          cause: causa,
        })
      }

      if (!respuesta.ok) {
        throw errorDelBorde(
          await mensajeDeError(
            respuesta,
            `bot-web respondió ${respuesta.status} al leer las entregas.`,
          ),
          respuesta.status,
        )
      }

      /**
       * ⭐⭐ `AW56` · **EL CASTEO ES DÉBIL A PROPÓSITO, Y ÉSE ES EL ARREGLO.**
       *
       * Hasta `AW56` acá decía `as PaginaDeEntregasEnLaRed`, y ese casteo **era
       * el mecanismo del hueco, no un detalle de estilo**: convencía a `tsc` de
       * que los campos de cada entrega estaban ahí, y con eso la comprobación
       * ausente **no se veía como ausente, se veía como innecesaria** — el
       * próximo que simplificara la habría borrado con razón aparente.
       *
       * ⇒ se castea a algo que **no le promete nada a `tsc`**, exactamente como
       * `enviar` ya hace con `{ readonly outcome?: unknown }`, y recién después
       * se comprueba. Es la misma disciplina, no una nueva.
       */
      const cuerpo = (await respuesta.json()) as {
        readonly entregas?: unknown
        readonly cursor?: unknown
        readonly hayMas?: unknown
      }

      /**
       * ⛔⛔ `AW56` · **LOS TRES DEL CONTENEDOR LANZAN; YA NO SE RELLENAN.**
       *
       * Antes los tres sustituían un valor de relleno —`[]`, el cursor que
       * traíamos, `false`— y **ninguno lanzaba nunca**. Una defensa que convierte
       * un fallo ruidoso en uno mudo no es una defensa: un `TypeError` se
       * diagnostica en un minuto, y una página que llega vacía porque el
       * contenedor cambió de nombre se reporta como «el bot no contesta» y manda
       * a buscar en el motor, en la base y en el ruteo.
       *
       * ⚠️ **Lanzar no reintroduce lo que el relleno del cursor evitaba.** Ese
       * relleno existía para no reiniciar la conversación desde el principio;
       * `chat.tsx` sólo adelanta `cursor.current` **cuando la lectura salió
       * bien**, así que con el `throw` el cursor **se queda donde estaba** igual
       * que antes, el sondeo reintenta en el próximo tic, y lo único que se suma
       * es que ahora se ve.
       */
      if (!Array.isArray(cuerpo.entregas)) {
        throw errorDelBorde(
          `bot-web contestó ${respuesta.status} sin un arreglo \`entregas\` en el cuerpo. El hilo no se puede mostrar y no se inventa.`,
          respuesta.status,
        )
      }

      /**
       * El cursor sale del cuerpo, SIEMPRE, y nunca se calcula acá. `bot-web` lo
       * declara *«siempre presente, aun sin entregas»* ⇒ que no esté es un
       * desencuentro del cable, y va dicho.
       */
      if (typeof cuerpo.cursor !== "string") {
        throw errorDelBorde(
          `bot-web contestó ${respuesta.status} sin \`cursor\` en el cuerpo, y lo declara siempre presente aun sin entregas. La lectura no puede seguir y no se reinicia desde el principio.`,
          respuesta.status,
        )
      }

      if (typeof cuerpo.hayMas !== "boolean") {
        throw errorDelBorde(
          `bot-web contestó ${respuesta.status} sin \`hayMas\` en el cuerpo. No se puede distinguir «me quedé al día» de «me cortaron», y no se adivina.`,
          respuesta.status,
        )
      }

      const crudas: readonly unknown[] = cuerpo.entregas

      return {
        entregas: crudas.map((crudo, indice) =>
          entregaDelCable(crudo, indice + 1, crudas.length, respuesta.status),
        ),
        cursor: cuerpo.cursor,
        hayMas: cuerpo.hayMas,
      }
    },

    /**
     * ⭐⭐ `ADR-0292` / `B22` · La lectura del registro de turnos.
     *
     * ⛔ **Los tres parámetros van SIEMPRE y ninguno se condiciona.** `desde` y
     * `limite` viajan como texto de un entero: el servicio contesta `400` ante
     * cualquier otra cosa, y eso es lo correcto — un `NaN` que el borde tapara con un
     * `0` volvería a leer el registro entero sin que nadie se entere.
     */
    leerTurnos: async (consulta: ConsultaDeTurnos): Promise<PaginaDeTurnosEnLaRed> => {
      const url = new URL(`${raiz}/turnos`)
      url.searchParams.set("canal", consulta.canal)
      url.searchParams.set("desde", String(consulta.desde))
      url.searchParams.set("limite", String(consulta.limite))

      let respuesta: Response
      try {
        respuesta = await fetch(url, {
          method: "GET",
          headers: { Accept: "application/json" },
          ...SIN_CREDENCIALES,
        })
      } catch (causa) {
        throw Object.assign(errorDelBorde(`No se pudo hablar con bot-web (${raiz}).`, 0), {
          cause: causa,
        })
      }

      if (!respuesta.ok) {
        throw errorDelBorde(
          await mensajeDeError(
            respuesta,
            `bot-web respondió ${respuesta.status} al leer el registro de turnos.`,
          ),
          respuesta.status,
        )
      }

      /**
       * ⛔⛔ **EL CASTEO ES DÉBIL, Y ESO NO ES ESTILO: ES EL MECANISMO DEL HUECO.**
       *
       * ~~`as PaginaDeTurnosEnLaRed`~~ convencía a `tsc` de que los campos
       * declarados ESTÁN, y con eso **la comprobación ausente no se ve como
       * ausente: se ve como innecesaria**. El tipo afirmaba lo que nadie había
       * verificado.
       *
       * ⭐ Es el mismo mecanismo que se pagó en `borde-participantes.ts` con el
       * signo cambiado: allá el tipo estaba bien y el parser DESCARTABA un campo
       * (`cruce`) que el contrato sí mandaba; acá el parser deja pasar cualquier
       * cosa y **el tipo miente por él**. Las dos veces el daño es el mismo: el
       * único renglón donde se podía ver el problema decía que no había problema.
       *
       * ⇒ se castea a `unknown` campo por campo —igual que el
       * `as { readonly outcome?: unknown }` de `enviar`, unas líneas más arriba— y
       * se comprueba DESPUÉS. Las tres comprobaciones de abajo ya existían; lo que
       * cambia es que ahora `tsc` las exige en vez de tolerarlas.
       */
      const cuerpo = (await respuesta.json()) as {
        readonly turnos?: unknown
        readonly ultimoSeq?: unknown
        readonly hayMas?: unknown
      }

      /**
       * ⛔ **`ultimoSeq` ausente ⇒ se vuelve a preguntar DESDE DONDE ESTÁBAMOS**, igual
       * que el `cursor` de `entregas` y por el mismo motivo: reiniciar en `0` relería
       * el registro entero y volvería a adjudicar turnos viejos a entregas nuevas.
       */
      return {
        turnos: Array.isArray(cuerpo.turnos) ? (cuerpo.turnos as readonly TurnoEnLaRed[]) : [],
        ultimoSeq: typeof cuerpo.ultimoSeq === "number" ? cuerpo.ultimoSeq : consulta.desde,
        hayMas: cuerpo.hayMas === true,
      }
    },
  }
}

/**
 * El borde de la instancia, o `null` si la instancia no declaró `botWebUrl`.
 *
 * ⭐ **Existe para que la compuerta de la pantalla sea UN `null` y no un `if`
 * repartido.** `App.tsx` decide con esto dos cosas a la vez —si monta la ruta y si
 * dibuja el renglón del menú— y las dos salen del mismo valor, así que no pueden
 * divergir. Es la misma disciplina que `brazo.ts` fijó para el parámetro de ruta:
 * un solo lector.
 */
export function bordeSimuladorDeLaInstancia(config: ConfiguracionInstancia): BordeSimulador | null {
  return config.botWebUrl === undefined ? null : crearBordeSimuladorHttp(config.botWebUrl)
}
